import { Fetching } from 'components/Fetching/Fetching';
import {
  Captcha,
  type CaptchaRef,
} from 'components/thirdParty/Captcha/Captcha';
import { Alert } from 'components/ui/Alert/Alert';
import { Button } from 'components/ui/Button/Button';
import { TextInput } from 'components/ui/TextInput/TextInput';
import { SENDER_EMAIL_FIELD } from 'constants/index';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'store/configureStore';
import type { RootState } from '../../reducers/types';
import { BackButton } from '../BackButton/BackButton';

import './UserLogin.scss';
import { logIn } from 'actions/authentication/authentication';
import { newNotification } from 'actions/ui/ui';
import { getFieldValue } from 'selectors/ui/ui';
import { useTranslations } from 'utils/translations/useTranslations';

const LOCKED_USER = 'locked_user';
const USER_NEEDS_TO_CHANGE_PASSWORD = 'user_needs_to_change_password';
const INVALID_CREDENTIALS = 'invalid_credentials';
const WAIT_TIME = 75;

type UserLoginProps = {
  firstInputRef?: React.MutableRefObject<HTMLInputElement>;
  handleSignUpClick: () => void;
  onLoginSuccess: () => void;
  onPrev: () => void;
  resetPassword?: (showUpdatePasswordAlert?: boolean) => void;
};
type LoginError = { error: string; errorDescription: string };
type LoginErrorResponse = Error & { parsedBody?: { errors: LoginError[] } };

function UserLogin({
  firstInputRef,
  handleSignUpClick,
  onLoginSuccess,
  onPrev,
  resetPassword,
}: UserLoginProps) {
  const i18n = useTranslations();
  const dispatch = useDispatch();
  const senderEmail = useSelector((state: RootState) =>
    getFieldValue(state, SENDER_EMAIL_FIELD),
  );
  const [username, setUsername] = useState(senderEmail);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<boolean | string>(false);
  const captchaRef = useRef<CaptchaRef>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: we need to run this on error
  useEffect(() => {
    const timer = setTimeout(() => {
      firstInputRef?.current?.focus();
    }, WAIT_TIME);

    return () => clearTimeout(timer);
  }, [error, firstInputRef]);

  const handleLogin = async (captchaResponse?: string) => {
    setError(false);

    try {
      await dispatch(
        logIn({
          username: username as string,
          password: password as string,
          captchaResponse,
        }),
      );
      dispatch(
        newNotification({
          message: i18n.t('notifications.login_user_success') as string,
          type: 'success',
        }),
      );
      onLoginSuccess();
    } catch (error: unknown) {
      const { parsedBody: { errors = [] } = {} } = error as LoginErrorResponse;

      const userNeedsToChangePassword = errors.find(
        item => item.error === USER_NEEDS_TO_CHANGE_PASSWORD,
      );

      const showUpdatePasswordAlert = true;
      if (userNeedsToChangePassword) {
        return resetPassword?.(showUpdatePasswordAlert);
      }

      const code = errors.find(item => item.error === LOCKED_USER);

      setError(code ? code.error : INVALID_CREDENTIALS);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (captchaRef.current?.isEnabled()) {
      await captchaRef.current?.execute();
    } else {
      await handleLogin();
    }
  };

  const handleChangeEmail = (_name: string, value: string) => {
    setUsername(value);
  };

  const handleChangePassword = (_name: string, value: string) => {
    setPassword(value);
  };

  const handleSubmitWithToken = async (token: string) => {
    if (token) {
      await handleLogin(token);
    } else {
      setError('captcha_error');
    }
  };

  return (
    <form
      data-testid="logInForm"
      onSubmit={handleSubmit}
      noValidate
      className="flex flex-col h-full"
    >
      <div className="flex-1">
        <BackButton onClick={onPrev} data-testid="backButton" />
        <Fetching entity="authentication" />
        <Captcha
          ref={captchaRef}
          onVerify={handleSubmitWithToken}
          data-testid="surchargeV2-Captcha"
        />
        <div className="UserLogin-heading">{i18n.t('userLogin.title')}</div>
        <div aria-live="assertive" role="alert">
          {error && (
            <Alert
              type="danger"
              id="userLogin-error-msg"
              aria-live="assertive"
              role="alert"
            >
              {i18n.t(`userLogin.alert.${error}`)}
            </Alert>
          )}
        </div>
        <div className="UserLogin-required">{i18n.t('userLogin.required')}</div>
        <div className="flex flex-col gap-8">
          <TextInput
            aria-describedby={error ? 'userLogin-error-msg' : undefined}
            autoFocus
            required
            error={!!error}
            data-testid="logInEmail"
            floatingLabel
            forwardRef={firstInputRef}
            label={i18n.t('userLogin.username') as string}
            name="username"
            onChange={handleChangeEmail}
            value={username as string}
          />
          <TextInput
            aria-describedby={error ? 'userLogin-error-msg' : undefined}
            required
            error={!!error}
            data-testid="logInPassword"
            floatingLabel
            label={i18n.t('userLogin.password') as string}
            name="logInPassword"
            onChange={handleChangePassword}
            type="password"
            value={password}
          />
        </div>
        <div className="UserLogin-recoverPassword">
          <button
            className="buttonLikeLink"
            onClick={() => resetPassword?.()}
            data-testid="recoverPassword"
            type="button"
          >
            {i18n.t('userLogin.recoverPassword')}
          </button>
        </div>
      </div>
      <div className="pt-6 pb-4">
        <Button
          type="submit"
          className="marginTop-xl"
          tone="primary"
          size="full"
          data-testid="logInButton"
        >
          {i18n.t('userLogin.button.login')}
        </Button>
        <div className="UserLogin-signup">
          <div className="UserLogin-question">
            {i18n.t('userLogin.question')}
          </div>
          <div className="UserLogin-link">
            <button onClick={handleSignUpClick}>
              {i18n.t('userLogin.signup.link')}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}

export { UserLogin };
